import { useEffect } from 'react';

import { useQueue } from 'react-use';

import { getAnonymousId } from 'utils/segment';
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setInLocalStorage,
} from 'utils/storage/storageHelper';

import { useNuDataDisabled } from './useNuDataDisabled';

const localStorageBackupKey = 'ndspmd';
const localStorageDidRefreshKey = 'did_refresh';

const nds = window.ndsapi || (window.ndsapi = {});

export function createNuDetectScript() {
  const baseUrl = `https://nd.chime.com/2.2/w/${window.Chime.config.NUDATA_CLIENT_ID}/sync/js/`;
  window.Chime.config.nuDetectLoaded = false;
  window.Chime.config.nuDetectReady = false;
  nds.config = {
    q: [],
    ready: function (cb) {
      window.Chime.config.nuDetectReady = true;
      cb();
      nds.reinit();
    },
  };
  const script = document.createElement('script');
  const firstScriptTagOnPage = document.getElementsByTagName('script')[0];
  script.src = baseUrl;
  firstScriptTagOnPage.parentNode.insertBefore(script, firstScriptTagOnPage);
  script.onload = function () {
    nds.load(baseUrl);
    window.Chime.config.nuDetectLoaded = true;
    nds.config.ready(() => {
      nds.setSessionId(getAnonymousId());
      nds.setPlacement('CreateAccount');
      nds.setPlacementPage(1);
    });
  };
}

const useNuDetect = () => {
  const { isNudataDisabled } = useNuDataDisabled();
  const disabled = isNudataDisabled();

  const {
    add: addCallback,
    first: nextCallback,
    remove: removeCallback,
    size: callbackQueueLength,
  } = useQueue();

  // Queue up events prior to NDS being ready (only if not disabled).
  useEffect(() => {
    if (disabled) {
      return;
    }
    if (window.Chime.config.nuDetectReady && callbackQueueLength > 0) {
      for (let i = 0; i < callbackQueueLength; i += 1) {
        nextCallback();
        removeCallback();
      }
    }
  }, [disabled, removeCallback, callbackQueueLength, nextCallback]);

  // Run NDS call or queue it up if NDS is not ready.
  const requestCall = cb => {
    if (disabled) {
      return;
    }
    if (window.Chime.config.nuDetectReady) {
      cb();
    } else {
      addCallback(cb);
    }
    saveBehaviouralData();
  };

  const beginBehaviouralMonitoring = () => {
    if (disabled) return;

    requestCall(() => {
      nds.setPlacementPage(window.ndsapi.config.placementPage + 1);
      nds.bindNewFields();
    });
  };

  const stopBehaviouralMonitoring = () => {
    if (disabled) return;

    saveBehaviouralData();
    requestCall(() => {
      nds.stop();
    });
  };

  const clearBehaviouralData = () => {
    if (disabled) return;

    requestCall(() => {
      nds.clear();
    });
  };

  return {
    beginBehaviouralMonitoring,
    clearBehaviouralData,
    requestCall,
    stopBehaviouralMonitoring,
  };
};

export const useBeginBehaviouralMonitoring = (dependency = []) => {
  const { beginBehaviouralMonitoring } = useNuDetect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(beginBehaviouralMonitoring, dependency);
};

export const saveBehaviouralData = () => {
  const ndspmdValue = document.getElementsByName('nds-pmd').item(0)?.value;
  if (ndspmdValue) {
    setInLocalStorage(localStorageBackupKey, ndspmdValue);
  }
};

export const getBehaviouralData = () =>
  getFromLocalStorage(localStorageBackupKey);

export const setDidRefresh = () =>
  setInLocalStorage(localStorageDidRefreshKey, true);

export const getDidRefresh = () => {
  const output = getFromLocalStorage(localStorageDidRefreshKey) === 'true';
  if (output) {
    removeFromLocalStorage(localStorageDidRefreshKey);
  }

  return output;
};

export default useNuDetect;
